import React, { useState, useEffect, useRef } from 'react';
import './header.css';
import Logo from '../../assets/images/logos/Logo_Trimergy_blanco.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { stripTrailingSlash } from '../../utils/pathNormalization';
import {
  SERVICIOS_ITEMS,
  PARA_QUIEN_ITEMS,
} from '../../config/landingNav';

/**
 * Sin JavaScript el navegador solo usa href. URLs absolutas al dominio público
 * garantizan navegación real en HTML estático / crawlers (evita /ruta rota en file:// o bases raras).
 * En desarrollo (sin origen) se mantienen rutas relativas al dev server.
 */
const SITE_ORIGIN = (
  process.env.REACT_APP_SITE_ORIGIN ||
  (process.env.NODE_ENV === 'production' ? 'https://trimergy.io' : '')
)
  .trim()
  .replace(/\/$/, '');

function landingNavHref(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (!SITE_ORIGIN) return p;
  return `${SITE_ORIGIN}${p}`;
}

function LandingNavLink({ to, className, children, afterNavigate, ...rest }) {
  const navigate = useNavigate();
  const href = landingNavHref(to);

  const handleClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.button !== 0) return;
    e.preventDefault();
    navigate(to);
    afterNavigate?.();
  };

  return (
    <a {...rest} href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}

function DesktopNavMegaMenu({ label, items, menuId, isActive, isOpen, onToggle, onClose }) {
  return (
    <div
      className={`nav-dropdown nav-dropdown--desktop ${isActive ? 'active' : ''} ${isOpen ? 'is-open' : ''}`}
    >
      <button
        type="button"
        className={`nav-link nav-dropdown__trigger ${isOpen ? 'is-open' : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls={`desktop-nav-panel-${menuId}`}
        onClick={() => onToggle(menuId)}
      >
        {label}
      </button>
      {/* Panel siempre en el HTML inicial: Google necesita los href reales
          (Geotrim, Ecotrim, Para quién) sin depender de un clic. */}
      <div
        id={`desktop-nav-panel-${menuId}`}
        className="nav-dropdown__panel"
        role="menu"
        hidden={!isOpen}
        aria-hidden={!isOpen}
      >
        <div className="nav-dropdown__panel-grid">
          <div className="nav-dropdown__section">
            <p className="nav-dropdown__section-title">{label}</p>
            <div className="nav-dropdown__section-divider" aria-hidden="true" />
            <div className="nav-dropdown__section-links">
              {items.map(({ label: itemLabel, path }) => (
                <LandingNavLink
                  key={path}
                  to={path}
                  className="nav-dropdown__item"
                  role="menuitem"
                  tabIndex={isOpen ? undefined : -1}
                  afterNavigate={onClose}
                >
                  {itemLabel}
                </LandingNavLink>
              ))}
            </div>
          </div>
          <div className="nav-dropdown__promo">
            <p className="nav-dropdown__promo-text">
              Descubre en 2 minutos cuánto podrás ahorrar añadiendo una batería.
            </p>
            <LandingNavLink
              to="/pricing/"
              className="nav-dropdown__promo-cta"
              tabIndex={isOpen ? undefined : -1}
              afterNavigate={onClose}
            >
              Ver planes
            </LandingNavLink>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileNavDropdown({ label, items, isActive, afterNavigate }) {
  return (
    <details className={`mobile-nav-dropdown ${isActive ? 'active' : ''}`}>
      <summary className="mobile-nav-link mobile-nav-dropdown__trigger">{label}</summary>
      <div className="mobile-nav-dropdown__menu">
        {items.map(({ label: itemLabel, path }) => (
          <LandingNavLink
            key={path}
            to={path}
            className="mobile-nav-link mobile-nav-dropdown__item"
            afterNavigate={afterNavigate}
          >
            {itemLabel}
          </LandingNavLink>
        ))}
      </div>
    </details>
  );
}

const Header = () => {
  const [activeLink, setActiveLink] = useState('inicio');
  const [scrolled, setScrolled] = useState(false);
  const [openDesktopMenu, setOpenDesktopMenu] = useState(null);
  const mobileMenuRef = useRef(null);
  const headerRef = useRef(null);
  const location = useLocation();

  const closeMobileMenu = () => {
    const el = mobileMenuRef.current;
    if (el) el.open = false;
  };

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const path = stripTrailingSlash(location.pathname);

    if (path === '/ecotrim' || path === '/geotrim') {
      setActiveLink('servicios');
    } else if (path.startsWith('/para-quien')) {
      setActiveLink('para-quien');
    } else if (path === '/calc' || path === '/calculadora') {
      setActiveLink('calc');
    } else if (path === '/pricing' || path === '/precios') {
      setActiveLink('pricing');
    } else if (path.startsWith('/blog')) {
      setActiveLink('blog');
    } else if (path === '/contact' || path === '/contacto') {
      setActiveLink('contacto');
    } else if (path === '/login') {
      setActiveLink('');
    } else {
      setActiveLink('');
    }
  }, [location.pathname]);

  useEffect(() => {
    setOpenDesktopMenu(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!openDesktopMenu) return undefined;

    const handlePointerDown = (event) => {
      if (headerRef.current?.contains(event.target)) return;
      setOpenDesktopMenu(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenDesktopMenu(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDesktopMenu]);

  const toggleDesktopMenu = (menuId) => {
    setOpenDesktopMenu((current) => (current === menuId ? null : menuId));
  };

  const closeDesktopMenu = () => {
    setOpenDesktopMenu(null);
  };

  return (
    <div className="landing-header-wrapper">
      <header ref={headerRef} className={`landing-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-logo">
          <LandingNavLink
            to="/"
            className="header-logo-link"
            aria-label="Inicio"
            afterNavigate={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img src={Logo} alt="Trimergy Logo" className="logo-image" />
          </LandingNavLink>
        </div>

        <nav className="header-nav" aria-label="Navegación principal">
          <DesktopNavMegaMenu
            label="Servicios"
            items={SERVICIOS_ITEMS}
            menuId="servicios"
            isActive={activeLink === 'servicios'}
            isOpen={openDesktopMenu === 'servicios'}
            onToggle={toggleDesktopMenu}
            onClose={closeDesktopMenu}
          />
          <DesktopNavMegaMenu
            label="Para quién"
            items={PARA_QUIEN_ITEMS}
            menuId="para-quien"
            isActive={activeLink === 'para-quien'}
            isOpen={openDesktopMenu === 'para-quien'}
            onToggle={toggleDesktopMenu}
            onClose={closeDesktopMenu}
          />
          <LandingNavLink
            to="/calc/"
            className={`nav-link ${activeLink === 'calc' ? 'active' : ''}`}
          >
            Calculadora
          </LandingNavLink>
          <LandingNavLink
            to="/pricing/"
            className={`nav-link ${activeLink === 'pricing' ? 'active' : ''}`}
          >
            Planes
          </LandingNavLink>
          <LandingNavLink
            to="/blog/"
            className={`nav-link ${activeLink === 'blog' ? 'active' : ''}`}
          >
            Blog
          </LandingNavLink>
          <LandingNavLink
            to="/contact/"
            className={`nav-link ${activeLink === 'contacto' ? 'active' : ''}`}
          >
            Contacto
          </LandingNavLink>
        </nav>

        <div className="header-actions">
          <div className="header-right">
            {!isAuthenticated && (
              <LandingNavLink className="register-button" to="/login/">
                Regístrate
              </LandingNavLink>
            )}
            {isAuthenticated && (
              <LandingNavLink className="register-button" to="/mi-area/">
                Mi area
              </LandingNavLink>
            )}
          </div>

          <details ref={mobileMenuRef} className="mobile-menu">
          <summary className="burger-button" aria-label="Abrir o cerrar menú de navegación">
            <span className="burger-line" />
            <span className="burger-line" />
            <span className="burger-line" />
          </summary>
          <nav className="mobile-nav" aria-label="Navegación móvil">
            <MobileNavDropdown
              label="Servicios"
              items={SERVICIOS_ITEMS}
              isActive={activeLink === 'servicios'}
              afterNavigate={closeMobileMenu}
            />
            <MobileNavDropdown
              label="Para quién"
              items={PARA_QUIEN_ITEMS}
              isActive={activeLink === 'para-quien'}
              afterNavigate={closeMobileMenu}
            />
            <LandingNavLink
              to="/calc/"
              className={`mobile-nav-link ${activeLink === 'calc' ? 'active' : ''}`}
              afterNavigate={closeMobileMenu}
            >
              Calculadora
            </LandingNavLink>
            <LandingNavLink
              to="/pricing/"
              className={`mobile-nav-link ${activeLink === 'pricing' ? 'active' : ''}`}
              afterNavigate={closeMobileMenu}
            >
              Planes
            </LandingNavLink>
            <LandingNavLink
              to="/blog/"
              className={`mobile-nav-link ${activeLink === 'blog' ? 'active' : ''}`}
              afterNavigate={closeMobileMenu}
            >
              Blog
            </LandingNavLink>
            <LandingNavLink
              to="/contact/"
              className={`mobile-nav-link ${activeLink === 'contacto' ? 'active' : ''}`}
              afterNavigate={closeMobileMenu}
            >
              Contacto
            </LandingNavLink>
          </nav>
          </details>
        </div>
      </header>
    </div>
  );
};

export default Header;
